import { Component, computed, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';

import { ROUTE_NAMES } from '@constants';
import { CommunityProfileType, PermissionLevel } from '@models';
import { AppInitService, BreakpointService } from '@services';
import { AiDockComponent } from '@shared/components/ai-assistant/ai-dock.component';
import { AppState, injectLocale } from '@store';
import { HeaderComponent } from './core/components/header.component';
import { NavigationProgressComponent } from './core/components/navigation-progress.component';
import { NavigationComponent } from './core/components/navigation.component';
import { ToastsContainerComponent } from './core/components/toasts-container.component';

const AUTH_ROUTES: ROUTE_NAMES[] = [
  ROUTE_NAMES.LOGIN,
  ROUTE_NAMES.REGISTRATION,
  ROUTE_NAMES.ACTIVATION,
];

@Component({
  selector: 'app-root',
  imports: [
    RouterModule,
    NavigationProgressComponent,
    ToastsContainerComponent,
    HeaderComponent,
    NavigationComponent,
    AiDockComponent,
  ],
  template: `
    <app-navigation-progress />
    <app-toasts-container />
    <app-ai-dock />
    @if (locale()) {
      <div
        class="app"
        [class]="{
          'navigation-expanded': navigation().open,
          'app--single-page': !shouldShowNavigation(),
        }"
      >
        @if (shouldShowHeader()) {
          <app-header class="grid-col-full" />
        }
        @if (shouldShowNavigation()) {
          <app-navigation
            [class.d-none]="!navigation().open"
            [class.d-md-flex]="!navigation().open"
          />
        }
        <main class="section overflow-auto">
          @if (!shouldShowHeader()) {
            <div class="logged-out-bg"></div>
          }
          <div
            [class.content]="shouldShowHeader()"
            [class.container-page]="shouldShowHeader()"
            [class.navigation-expanded]="navigation().open"
            [class.d-none]="navigation().open && breakpoint.below('md')()"
            [class.width-100]="
              community().communityProfileId ===
                CommunityProfileType.SIGNING_ROOM &&
              !community().communityPermission
            "
            [class.ms-0]="
              community().communityProfileId ===
                CommunityProfileType.SIGNING_ROOM &&
              !community().communityPermission
            "
          >
            <router-outlet />
          </div>
        </main>
      </div>
    }
  `,
  styles: `
    .logged-out-bg {
      position: absolute;
      z-index: -1;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background-repeat: no-repeat;
      background-position: top center;
      background-size: cover;
      background-attachment: fixed;
      background-image: url('/assets/images/bg/bg_swisscom.jpeg');

      &::after {
        content: '';
        position: absolute;
        z-index: 2;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFBAMAAAB/QTvWAAAAElBMVEUAAAAAAAAAAAAAAAAAAAAAAADgKxmiAAAABnRSTlMAAQIdISK0zoKOAAAAHElEQVQIHWMQdmJgMFFQYHBiCmBQUDVgYHIWAAAVkQIoeqolhAAAAABJRU5ErkJggg==');
      }
    }
  `,
})
export class AppComponent implements OnInit {
  store = inject<Store<AppState>>(Store);
  appInitService = inject(AppInitService);

  user = toSignal(
    this.store.select((state) => state.user),
    {
      requireSync: true,
    }
  );
  community = toSignal(
    this.store.select((state) => state.community),
    {
      requireSync: true,
    }
  );
  locale = injectLocale();
  navigation = toSignal(
    this.store.select((state) => state.navigation),
    {
      requireSync: true,
    }
  );

  breakpoint = inject(BreakpointService);
  CommunityProfileType = CommunityProfileType;

  ngOnInit() {
    this.appInitService.initializeApp();
  }

  isAuthRoute = computed(
    () =>
      this.navigation().route !== undefined &&
      AUTH_ROUTES.includes(this.navigation().route!)
  );

  shouldShowHeader = computed(
    () => this.user().userId !== undefined && !this.isAuthRoute()
  );

  shouldShowNavigation = computed(() => {
    const { communityProfileId, communityPermission } = this.community();

    return (
      this.shouldShowHeader() &&
      (communityProfileId !== CommunityProfileType.SIGNING_ROOM ||
        (communityProfileId === CommunityProfileType.SIGNING_ROOM &&
          communityPermission === PermissionLevel.MODIFY_AND_DELETE))
    );
  });
}
